import { useEffect, useRef } from "react";
import { Button } from "../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import {
  isPendingSourceGoogleSheet,
  stashPendingConnectionFromWindowName,
} from "../lib/pending-connection";

let setupStarted = false;

export default function Setup() {
  stashPendingConnectionFromWindowName();

  const { isAuthenticated, user, loginAsGuest, error, logout } = useAuth();
  const navigate = useNavigate();
  const cancelledRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const signedIn = Boolean(user);
  // Beta sign-up gate (board answer Q8, folded in from MOSA-162): the website
  // hand-off to `#/setup` with a Google Sheet must not create a guest session.
  // The pending value stays in sessionStorage so the connect dialog can open
  // it after sign-up or login; a Postgres string keeps the guest path.
  const sheetNeedsSignUp = !signedIn && isPendingSourceGoogleSheet();

  useEffect(() => {
    if (cancelledRef.current) return;
    if (isAuthenticated) return;
    if (sheetNeedsSignUp) return;
    if (setupStarted) return;
    setupStarted = true;
    const controller = new AbortController();
    abortRef.current = controller;
    loginAsGuest("", undefined, { signal: controller.signal }).catch(() => {
      setupStarted = false;
    });
    return () => {
      abortRef.current = null;
    };
  }, [isAuthenticated, loginAsGuest, sheetNeedsSignUp]);

  useEffect(() => {
    if (sheetNeedsSignUp) {
      cancelledRef.current = true;
      setupStarted = false;
      abortRef.current?.abort();
      navigate("/auth", { replace: true });
    }
  }, [sheetNeedsSignUp, navigate]);

  const goAuth = () => {
    cancelledRef.current = true;
    setupStarted = false;
    abortRef.current?.abort();
    navigate("/auth");
  };

  const goDataSources = () => {
    navigate("/data-sources");
  };

  const goLogin = () => {
    cancelledRef.current = true;
    setupStarted = false;
    abortRef.current?.abort();
    logout();
    navigate("/auth");
  };

  const waiting = !isAuthenticated && !error && !sheetNeedsSignUp;

  if (sheetNeedsSignUp) {
    return (
      <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
        <div
          className="max-w-md w-full bg-od-surface border border-od-border rounded-2xl p-8 space-y-5 text-center"
          data-testid="setup-signup-gate"
        >
          <h1 className="text-2xl font-semibold text-od-text">
            Sign up to connect a Google Sheet
          </h1>
          <p className="text-sm text-od-muted">
            Guests can use the demo. To connect a Google Sheet, create an
            account. OnlyDash keeps your Sheet link and opens it after you sign
            up.
          </p>
          <Button
            onClick={goAuth}
            variant="primary"
            className="w-full text-sm"
          >
            Sign up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-od-surface border border-od-border rounded-2xl p-8 space-y-5 text-center">
        <h1 className="text-2xl font-semibold text-od-text">
          {isAuthenticated && !error ? "Session ready" : "Set up your session"}
        </h1>
        {error ? (
          <p className="text-sm text-od-text">{error}</p>
        ) : waiting ? (
          <div className="space-y-3">
            <div
              className="mx-auto h-8 w-8 rounded-full border-2 border-od-muted border-t-od-primary animate-spin"
              aria-hidden
            />
            <p className="text-sm text-od-muted" role="status">
              Creating your guest session…
            </p>
          </div>
        ) : (
          <p className="text-sm text-od-muted">Your session is ready.</p>
        )}
        {isAuthenticated && !error && (
          <Button
            onClick={goDataSources}
            variant="primary" className="w-full text-sm"
          >
            Go to data sources
          </Button>
        )}
        <div className="flex flex-col items-center gap-2">
          {waiting && (
            <Button
              onClick={goAuth}
              variant="link" size="sm" className="text-sm"
            >
              Cancel
            </Button>
          )}
          {!waiting && (
            <Button
              onClick={goLogin}
              variant="link" size="sm" className="text-sm text-od-primary-text hover:text-od-primary-text hover:underline"
            >
              Back to sign in
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
